/**
 * Il cambiamento di un attributo che è un **elenco** (le LUN di un array, le
 * porte, gli initiator, il software), detto per elemento.
 *
 * Lo storico di un CI mostrava i due valori interi uno accanto all'altro: su
 * sedici LUN a cui si era aggiunto il WWN erano due blocchi di JSON lunghi una
 * pagina, identici tranne che in sedici punti da trovare a occhio. La domanda
 * vera è un'altra — «cosa è entrato, cosa è uscito, cosa è cambiato in chi» — e
 * la risposta sta in una riga: «16 elementi: aggiunto wwn».
 *
 * Gli elementi si abbinano per una **chiave naturale** presente in tutti (id,
 * WWN, nome, MAC, indirizzo…): abbinarli per posizione farebbe di un elemento
 * inserito in testa «tutti cambiati». Senza una chiave comune si confrontano
 * interi, e si sa solo cosa è entrato e cosa è uscito.
 */

export type Elemento = unknown

export interface CampoCambiato {
  campo: string
  da: unknown
  a: unknown
}

export interface ElementoCambiato {
  chiave: string
  campi: CampoCambiato[]
}

export interface DiffLista {
  aggiunti: Elemento[]
  tolti: Elemento[]
  cambiati: ElementoCambiato[]
  /** Il campo con cui si sono abbinati gli elementi, se ce n'era uno. */
  chiave: string | null
}

/** Le chiavi candidate, nell'ordine in cui si provano. */
const CHIAVI = [
  'id', 'wwn', 'uuid', 'serial', 'seriale', 'mac', 'if_name', 'name', 'nome',
  'ip', 'address', 'key', 'path', 'posizione', 'location',
]

function eOggetto(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function firma(v: unknown): string {
  if (!eOggetto(v)) return JSON.stringify(v)
  const ordinato: Record<string, unknown> = {}
  for (const k of Object.keys(v).sort()) ordinato[k] = v[k]
  return JSON.stringify(ordinato)
}

function chiaveComune(elementi: unknown[]): string | null {
  if (!elementi.length || !elementi.every(eOggetto)) return null
  for (const k of CHIAVI) {
    const valori = elementi.map((e) => (e as Record<string, unknown>)[k])
    if (valori.every((v) => v !== undefined && v !== null && v !== '') && new Set(valori.map(String)).size === valori.length) {
      return k
    }
  }
  return null
}

/**
 * Il diff di due elenchi, o `null` se i due valori non sono elenchi (allora
 * vale la resa normale, vecchio → nuovo).
 */
export function diffLista(prima: unknown, dopo: unknown): DiffLista | null {
  const a = prima == null ? [] : prima
  const b = dopo == null ? [] : dopo
  if (!Array.isArray(a) || !Array.isArray(b)) return null
  if (prima == null && dopo == null) return null

  const chiave = chiaveComune([...a, ...b])
  if (chiave) {
    const perChiave = (lista: unknown[]) =>
      new Map(lista.map((e) => [String((e as Record<string, unknown>)[chiave]), e as Record<string, unknown>]))
    const va = perChiave(a)
    const vb = perChiave(b)
    const cambiati: ElementoCambiato[] = []
    for (const [k, nuovo] of vb) {
      const vecchio = va.get(k)
      if (!vecchio) continue
      const campi: CampoCambiato[] = []
      for (const campo of new Set([...Object.keys(vecchio), ...Object.keys(nuovo)])) {
        if (firma(vecchio[campo]) !== firma(nuovo[campo])) {
          campi.push({ campo, da: vecchio[campo], a: nuovo[campo] })
        }
      }
      if (campi.length) cambiati.push({ chiave: k, campi })
    }
    return {
      aggiunti: [...vb.keys()].filter((k) => !va.has(k)).map((k) => vb.get(k)),
      tolti: [...va.keys()].filter((k) => !vb.has(k)).map((k) => va.get(k)),
      cambiati,
      chiave,
    }
  }

  // Senza una chiave comune: interi, come multinsiemi.
  const conta = (lista: unknown[]) => {
    const m = new Map<string, { n: number; e: unknown }>()
    for (const e of lista) {
      const f = firma(e)
      const voce = m.get(f)
      if (voce) voce.n++
      else m.set(f, { n: 1, e })
    }
    return m
  }
  const ca = conta(a)
  const cb = conta(b)
  const aggiunti: unknown[] = []
  const tolti: unknown[] = []
  for (const [f, { n, e }] of cb) for (let i = 0; i < n - (ca.get(f)?.n ?? 0); i++) aggiunti.push(e)
  for (const [f, { n, e }] of ca) for (let i = 0; i < n - (cb.get(f)?.n ?? 0); i++) tolti.push(e)
  return { aggiunti, tolti, cambiati: [], chiave: null }
}

/** I campi cambiati, riassunti su tutti gli elementi: campo → come. */
export function campiRiassunti(d: DiffLista): { campo: string; come: 'aggiunto' | 'tolto' | 'cambiato'; quanti: number }[] {
  const conteggi = new Map<string, { come: 'aggiunto' | 'tolto' | 'cambiato'; quanti: number }>()
  for (const el of d.cambiati) {
    for (const c of el.campi) {
      const come = c.da === undefined || c.da === null ? 'aggiunto' : c.a === undefined || c.a === null ? 'tolto' : 'cambiato'
      const k = `${c.campo}|${come}`
      const voce = conteggi.get(k)
      if (voce) voce.quanti++
      else conteggi.set(k, { come, quanti: 1 })
    }
  }
  return [...conteggi.entries()]
    .map(([k, v]) => ({ campo: k.split('|')[0], ...v }))
    .sort((x, y) => y.quanti - x.quanti || x.campo.localeCompare(y.campo))
}

/** Come si chiama un elemento nel dettaglio: la sua chiave, o un nome. */
export function nomeElemento(e: unknown, chiave: string | null): string {
  if (eOggetto(e)) {
    const k = chiave && e[chiave] != null ? e[chiave] : e.name ?? e.nome ?? e.id ?? e.wwn ?? e.mac ?? e.ip
    if (k != null) return String(k)
    return JSON.stringify(e).slice(0, 80)
  }
  return String(e)
}
